'use client';
import { useRef } from 'react';
import { OverlayCardSlder, Slider } from '../Slider';
import { Col, Flex, Row } from 'antd';
import Heading from '../Heading';
import { useRouter } from 'next/navigation';
import { PageVenueFragment } from '@/@types/generated/sitecore';
import CustButton, { GeneralButtonType } from '@/components/Button';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import WaveRightIcon from '../SVG/WaveRightIcon';
import WaveLeftIcon from '../SVG/WaveLeftIcon';
import { WATrackerClickEvn, getButtonLinkWithCID } from '@/utils/wa';
import { mobileBreakPoint } from '@/utils/utils';
import useWindowSize from '@/utils/useScreenSize';
import { DiningCardType, OverlayCard } from '../Template/Card';
import RichText from '../RichText';
import useALTracker, { EventClickType } from '@/utils/useALTracker';

interface Props {
    content: PageVenueFragment;
    data: {
        partyDateFolder: any;
        partyDetail: any;
        partyInformation: any;
    };
}

/** 适用于 pageDetail-PartyVenue  */
const PartyVenue = (props: Props) => {
    const { partyInformation } = props.data;
    const router = useRouter();
    const globalStore = useGlobalStore();
    const venueRef = useRef<HTMLDivElement>(null);
    const { ALTrackClickEvent } = useALTracker();

    const themeColor =
        globalStore.globalSettingData?.masterColorCode?.targetItem?.themeColorCode?.value;

    const partyThemeColor = partyInformation?.partyColorCode?.targetItem?.themeColorCode?.value;

    const musicWaveColor = globalStore.currentTheme?.musicWaveColor;

    const h2Color = globalStore.currentTheme?.h2Color;

    const partyH2Color = partyInformation?.partyColorCode?.targetItem?.h2Color?.value;

    const currentThemeColor = partyThemeColor ? partyThemeColor : themeColor;

    const TITLE_ICON = {
        titleIconRight: () => <WaveRightIcon iconColor={musicWaveColor}></WaveRightIcon>,
        titleIconLeft: () => <WaveLeftIcon iconColor={musicWaveColor}></WaveLeftIcon>,
    };

    const TitleIcon = props.content?.titleIconOptions?.value
        ? TITLE_ICON[props.content?.titleIconOptions?.value as keyof typeof TITLE_ICON]
        : () => null;

    const isMobile = useWindowSize().width < 768;

    return (
        <Row
            ref={venueRef}
            align={'middle'}
            className={'w-full relative xmd:my-0 bg-secondary-bg-color my-8'}
        >
            <div
                className={`absolute top-0 left-0 right-0 bottom-0 bg-wave bg-no-repeat ${isMobile ? 'bg-[position:-107px_0px] bg-[length:250%_55%]' : 'bg-[position:500px_-250px]'} `}
            />
            <Col xs={24} md={12}>
                <Flex
                    vertical
                    gap={24}
                    justify={'center'}
                    align={'start'}
                    className={'mx-5 my-8 xmd:py-32 xmd:pl-20 xmd:pr-14'}
                >
                    <Flex vertical gap={8}>
                        <Heading
                            className={'!text-base-font-color'}
                            level={5}
                            text={props.content?.blockSubHeader?.value}
                        />
                        <TitleIcon />
                        <Heading
                            style={{ color: partyH2Color || h2Color }}
                            level={2}
                            text={props.content?.blockHeader?.value}
                        />
                    </Flex>
                    <Flex vertical>
                        <RichText
                            className='text-body1 leading-body1 text-white'
                            html={props.content?.blockContent?.rendered || ''}
                        />
                    </Flex>
                    {props.content?.buttonEnable?.boolValue && (
                        <CustButton
                            data={{
                                setting: {
                                    onClick: () => {
                                        ALTrackClickEvent(
                                            'RnE/HW_ClickBanner_Feature-Highlight-with-slider',
                                            {
                                                eventType: EventClickType.clickBanner,
                                                url: getButtonLinkWithCID(
                                                    props.content?.buttonLink?.value,
                                                    `/party/Detail_Feature-Highlight-with-slider`,
                                                ),
                                                _pageId: `RnE/HW/Party/${(globalStore?.pageID)?.replace('parties/', '')}`
                                            }
                                        );
                                        // WATrackerClickEvn(
                                        //     'RnE/HW_ClickBanner_Feature-Highlight-with-slider',
                                        // );
                                        // showModal(),
                                        // router.push(
                                        //     `${props.content?.buttonLink?.value}${location.search}`,
                                        // );
                                    },
                                },
                                buttonLink: getButtonLinkWithCID(
                                    props.content?.buttonLink?.value,
                                    `parties/${partyInformation?.partyName?.value || 'Detail'}_Feature-Highlight-with-slider`,
                                ),
                            }}
                        >
                            <GeneralButtonType
                                label={props.content?.buttonText?.value || ''}
                                icon={'arrowRightBK'}
                            />
                        </CustButton>
                    )}
                </Flex>
            </Col>
            <Col xs={24} md={12} className={'px-5'}>
                <Row gutter={20}>
                    {!isMobile ? (
                        props.content?.displayVenue?.venueList &&
                        props.content?.displayVenue?.venueList.length > 2 ? (
                            <Slider
                                settings={{
                                    pagination: {
                                        clickable: true,
                                    },
                                    spaceBetween: 10,
                                    breakpoints: {
                                        1280: {
                                            slidesPerView: 2.5,
                                        },
                                        768: {
                                            slidesPerView: 1.5,
                                        },
                                    },
                                    // autoplay: {
                                    //     delay: 10000,
                                    //     disableOnInteraction: false,
                                    // },
                                }}
                                className='w-full'
                                currentThemeColor={currentThemeColor}
                            >
                                {OverlayCardSlder({
                                    data: props.content?.displayVenue?.venueList,
                                    currentThemeColor,
                                    currentH2Color: partyH2Color || h2Color,
                                })}
                            </Slider>
                        ) : (
                            <>
                                {props.content?.displayVenue?.venueList &&
                                    props.content?.displayVenue?.venueList.map((item, index) => (
                                        <Col xs={24} md={12} key={index}>
                                            <OverlayCard
                                                data={item}
                                                style={{
                                                    containerMinHeightSm: 'min-h-[415px]',
                                                    borderSm: 'rounded-[4px]',
                                                }}
                                                currentThemeColor={currentThemeColor}
                                            >
                                                <DiningCardType
                                                    currentThemeColor={currentThemeColor}
                                                    data={item}
                                                    currentH2Color={partyH2Color || h2Color}
                                                />
                                            </OverlayCard>
                                        </Col>
                                    ))}
                            </>
                        )
                    ) : (
                        <Col span={24}>
                            <Slider
                                settings={{
                                    slidesPerView: 1,
                                    pagination: {
                                        clickable: true,
                                    },
                                }}
                                className='w-full'
                                currentThemeColor={currentThemeColor}
                            >
                                {OverlayCardSlder({
                                    data: props.content?.displayVenue?.venueList,
                                    currentThemeColor,
                                    currentH2Color: partyH2Color || h2Color,
                                })}
                            </Slider>
                        </Col>
                    )}
                </Row>
            </Col>
        </Row>
    );
};

export default PartyVenue;
